import React, { useState } from 'react';
import { connect } from 'react-redux';
import { updateUserInfo, processExtractedSchedule } from '../actions/auth';
import { getCookie } from '../utils/cookies';
import { FiUpload } from 'react-icons/fi';
import toast from 'react-hot-toast';

const UserInfoForm = ({ updateUserInfo, processExtractedSchedule, currentCampus, currentYear, currentPhone, email }) => {
    const [formData, setFormData] = useState({
        campus: currentCampus || '',
        year: currentYear || '',
        phone: (currentPhone && currentPhone !== 'N/A') ? currentPhone : ''
    });
    const [phonePreferences, setPhonePreferences] = useState({
        text: false,
        call: false
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadStatus, setUploadStatus] = useState('');
    const [isDragging, setIsDragging] = useState(false);

    const campusOptions = [
        { value: 'NB', label: 'New Brunswick' },
        { value: 'NK', label: 'Newark' },
        { value: 'CAC', label: 'Camden' }
    ];

    const yearOptions = [
        { value: 'Freshman', label: 'Freshman' },
        { value: 'Sophomore', label: 'Sophomore' },
        { value: 'Junior', label: 'Junior' },
        { value: 'Senior', label: 'Senior' },
        { value: 'Graduate', label: 'Graduate' }
    ];

    const generateUniqueFileName = (originalFileName) => {
        const timestamp = new Date().getTime();
        const randomString = Math.random().toString(36).substring(2, 8);
        const extension = originalFileName.split('.').pop();
        return `${timestamp}-${randomString}.${extension}`;
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (file && file.type.startsWith('image/')) {
            setSelectedFile(file);
            setUploadStatus('');
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file && file.type.startsWith('image/')) {
            setSelectedFile(file);
            setUploadStatus('');
        }
    };

    const handleScheduleUpload = async () => {
        if (!selectedFile || uploadStatus === 'getting-url' || uploadStatus === 'uploading' || uploadStatus === 'processing') {
            return false;
        }

        const waitingToast = toast.loading(
            <div className="flex flex-col">
                <span className="text-lg font-bold">Uploading Schedule</span>
                <span className="text-sm">Please wait while we process your schedule...</span>
            </div>,
            {
                position: 'top-center',
                style: {
                    background: '#3B82F6',
                    color: 'white',
                    padding: '16px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                },
            }
        );

        try {
            setUploadStatus('getting-url');

            // Generate unique filename
            const uniqueFileName = generateUniqueFileName(selectedFile.name);
            const s3Key = `uploads/${uniqueFileName}`;
            const contentType = selectedFile.type || 'image/png';

            const presignResponse = await fetch('https://api.ruturbo.com/process-schedule', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: getCookie('session_id'),
                },
                body: JSON.stringify({
                    email: email,
                    action: 'generateUrl',
                    key: s3Key,
                    contentType: contentType
                }),
            });

            if (!presignResponse.ok) {
                throw new Error(`Failed to get URL: ${presignResponse.status}`);
            }

            const responseData = await presignResponse.json();
            const presignedUrl = responseData.url;
            
            if (!presignedUrl) {
                throw new Error('No URL returned from server');
            }

            // Upload to S3
            setUploadStatus('uploading');
            const uploadResult = await fetch(presignedUrl, {
                method: 'PUT',
                body: selectedFile
            });

            if (!uploadResult.ok) {
                throw new Error(`S3 upload failed: ${uploadResult.status}`);
            }

            // Process the uploaded image
            setUploadStatus('processing');
            const processResponse = await fetch('https://api.ruturbo.com/process-schedule', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: getCookie('session_id'),
                },
                body: JSON.stringify({
                    email: email,
                    action: 'extractText',
                    key: s3Key,
                }),
            });

            if (!processResponse.ok) {
                throw new Error(`Processing failed: ${processResponse.status}`);
            }

            const processData = await processResponse.json();
            
            console.log('Schedule processing response:', processData);
            
            setUploadStatus('done');
            toast.dismiss(waitingToast);
            
            // Process the extracted course codes and add to calendar
            if (processData.CurrentClasses && Array.isArray(processData.CurrentClasses)) {
                console.log('Calling processExtractedSchedule with:', processData.CurrentClasses);
                await processExtractedSchedule(processData.CurrentClasses);
            } else {
                console.warn('No CurrentClasses found in response:', processData);
            }
            
            toast.success(
                <div className="flex flex-col">
                    <span className="text-lg font-bold">Schedule Uploaded</span>
                    <span className="text-sm">Your schedule has been successfully processed</span>
                </div>,
                {
                    duration: 4000,
                    position: 'top-center',
                }
            );

            return true;
        } catch (err) {
            console.error('Error:', err);
            setUploadStatus('error');
            toast.dismiss(waitingToast);
            toast.error(
                <div className="flex flex-col">
                    <span className="text-lg font-bold">Upload Failed</span>
                    <span className="text-sm">{err.message || 'Failed to process schedule. Please try again.'}</span>
                </div>,
                {
                    duration: 4000,
                    position: 'top-center',
                    style: {
                        background: '#EF4444',
                        color: 'white',
                        padding: '16px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    },
                }
            );
            return false;
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate required fields
        if (!formData.campus || !formData.year) {
            toast.error(
                <div className="flex flex-col">
                    <span className="text-lg font-bold">Missing Information</span>
                    <span className="text-sm">Please fill out all required fields</span>
                </div>,
                {
                    duration: 4000,
                    position: 'top-center',
                    style: {
                        background: '#EF4444',
                        color: 'white',
                        padding: '16px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba (0, 0, 0, 0.1)',
                    },
                }
            );
            return;
        }

        // Validate phone number format (only if provided and not empty/N/A)
        const phoneValue = formData.phone ? formData.phone.trim() : '';
        if (phoneValue && phoneValue !== '' && phoneValue !== 'N/A') {
            const phoneRegex = /^\d{10}$/;
            if (!phoneRegex.test(phoneValue)) {
                toast.error(
                    <div className="flex flex-col">
                        <span className="text-lg font-bold">Invalid Phone Format</span>
                        <span className="text-sm">Please use format: 1234567890 (10 digits) or leave blank</span>
                    </div>,
                    {
                        duration: 4000,
                        position: 'top-center',
                        style: {
                            background: '#EF4444',
                            color: 'white',
                            padding: '16px',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        },
                    }
                );
                return;
            }
        }

        // Check if schedule upload is required but not completed
        if (!selectedFile) {
            toast.error(
                <div className="flex flex-col">
                    <span className="text-lg font-bold">Schedule Required</span>
                    <span className="text-sm">Please upload your current schedule</span>
                </div>,
                {
                    duration: 4000,
                    position: 'top-center',
                    style: {
                        background: '#EF4444',
                        color: 'white',
                        padding: '16px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    },
                }
            );
            return;
        }

        setIsSubmitting(true);

        // First update user info (this sets the campus in Redux)
        const userInfoToSend = {
            ...formData,
            text: phonePreferences.text,
            call: phonePreferences.call
        };
        const userInfoSuccess = await updateUserInfo(userInfoToSend);
        
        if (!userInfoSuccess) {
            setIsSubmitting(false);
            return;
        }

        // Then upload the schedule (needs campus to be set)
        const scheduleUploadSuccess = await handleScheduleUpload();
        setIsSubmitting(false);
        
        if (scheduleUploadSuccess) {
            // Additional success message for completing everything
            toast.success(
                <div className="flex flex-col">
                    <span className="text-lg font-bold">Profile Complete!</span>
                    <span className="text-sm">Your information and schedule have been updated successfully</span>
                </div>,
                {
                    duration: 4000,
                    position: 'top-center',
                }
            );
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-md mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white z-10 p-6 pb-4 border-b border-gray-200">
                    <div className="text-center">
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            Complete Your Profile
                        </h2>
                        <p className="text-gray-600">
                            Please provide the following required information to continue using RUTurbo
                        </p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {/* Campus Selection */}
                    <div>
                        <label htmlFor="campus" className="block text-sm font-medium text-gray-700 mb-1">
                            Campus <span className="text-red-500">*</span>
                        </label>
                        <select
                            id="campus"
                            name="campus"
                            value={formData.campus}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                        >
                            <option value="">Select your campus</option>
                            {campusOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Year Selection */}
                    <div>
                        <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-1">
                            Academic Year <span className="text-red-500">*</span>
                        </label>
                        <select
                            id="year"
                            name="year"
                            value={formData.year}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                        >
                            <option value="">Select your year</option>
                            {yearOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Phone Number */}
                    <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                            Phone Number <span className="text-gray-400">(Optional)</span>
                        </label>
                        <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="1234567890"
                            maxLength="10"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Format: 1234567890 (10 digits - Optional - for notifications)
                        </p>
                        
                        {/* Phone Preferences Checkboxes */}
                        {formData.phone && formData.phone.length === 10 && (
                            <div className="mt-3 space-y-2">
                                <p className="text-sm text-gray-700 font-medium">Notification Preferences:</p>
                                <div className="flex space-x-4">
                                    <label className="flex items-center space-x-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={phonePreferences.text}
                                            onChange={(e) => setPhonePreferences(prev => ({ ...prev, text: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded 
                                                focus:ring-blue-500 focus:ring-2"
                                        />
                                        <span className="text-gray-700 text-sm">Text</span>
                                    </label>
                                    
                                    <label className="flex items-center space-x-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={phonePreferences.call}
                                            onChange={(e) => setPhonePreferences(prev => ({ ...prev, call: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded 
                                                focus:ring-blue-500 focus:ring-2"
                                        />
                                        <span className="text-gray-700 text-sm">Call</span>
                                    </label>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Schedule Upload Section */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Upload Your Current Schedule <span className="text-red-500">*</span>
                        </label>
                        
                        {/* Instructions */}
                        <div className="mb-2 bg-blue-50 border border-blue-200 rounded p-2">
                            <p className="text-xs text-gray-700 mb-1">
                                <span className="font-semibold text-blue-700">Tip:</span> on WebReg Screenshot your Schedule in Manage Registration tab making sure <span className="font-semibold">index numbers are visible</span>
                            </p>
                            
                            {/* Example Image - Very Compact */}
                            <details className="text-xs">
                                <summary className="text-blue-600 cursor-pointer hover:text-blue-700">View example</summary>
                                <div className="mt-2 w-full max-w-md rounded border border-gray-300 overflow-hidden bg-white">
                                    <img 
                                        src="/example-schedule.png" 
                                        alt="Example schedule screenshot" 
                                        className="w-full"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'block';
                                        }}
                                    />
                                    <div className="hidden text-center p-3 text-gray-500 text-xs">
                                        Example image placeholder
                                    </div>
                                </div>
                            </details>
                        </div>
                        
                        <div
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            className={`relative border-2 border-dashed rounded-md p-6 text-center transition-colors ${
                                isDragging
                                    ? 'border-blue-400 bg-blue-50'
                                    : 'border-gray-300 hover:border-gray-400 bg-gray-50'
                            }`}
                        >
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleFileSelect}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                disabled={uploadStatus === 'getting-url' || uploadStatus === 'uploading' || uploadStatus === 'processing'}
                            />
                            <div className="space-y-2">
                                <div className="flex justify-center">
                                    <FiUpload className={`w-8 h-8 ${isDragging ? 'text-blue-400' : 'text-gray-400'}`} />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-600">
                                        {selectedFile
                                            ? selectedFile.name
                                            : 'Drag and drop your WebReg screenshot here'}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {selectedFile
                                            ? `${(selectedFile.size / 1024 / 1024).toFixed(2)} MB - Ready to upload`
                                            : 'or click to browse (PNG, JPG, JPEG accepted)'}
                                    </p>
                                </div>
                                {uploadStatus && (
                                    <div className="mt-2">
                                        <p className={`text-xs font-medium ${
                                            uploadStatus === 'done' ? 'text-green-600' : 
                                            uploadStatus === 'error' ? 'text-red-600' : 'text-blue-600'
                                        }`}>
                                            {uploadStatus === 'getting-url' && 'Getting upload URL...'}
                                            {uploadStatus === 'uploading' && 'Uploading schedule...'}
                                            {uploadStatus === 'processing' && 'Processing schedule...'}
                                            {uploadStatus === 'done' && 'Schedule processed successfully!'}
                                            {uploadStatus === 'error' && 'Upload failed - please try again'}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                            Please upload a screenshot of your current WebReg schedule. This helps administrators add new sections at convenient times for you.
                        </p>
                    </div>

                    {/* Submit Button */}
                    <div className="pt-4">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className={`w-full py-3 px-4 rounded-md text-white font-medium transition-colors duration-200 ${
                                isSubmitting
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                            }`}
                        >
                            {isSubmitting ? 'Updating...' : 'Update Profile'}
                        </button>
                    </div>

                    <div className="text-center">
                        <p className="text-xs text-gray-500">
                            Fields marked with <span className="text-red-500">*</span> are required. Phone number is optional but recommended for notifications.
                        </p>
                    </div>
                </form>
            </div>
        </div>
    );
};

const mapStateToProps = (state) => ({
    currentCampus: state.profile.campus,
    currentYear: state.profile.year,
    currentPhone: state.profile.phone,
    email: state.profile.email
});

const mapDispatchToProps = {
    updateUserInfo,
    processExtractedSchedule
};

export default connect(mapStateToProps, mapDispatchToProps)(UserInfoForm);